import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { IllamAppIcon, IllamLogo, IllamMark } from "@/components/IllamLogo";
import { LivingRoomArt, KitchenArt, BedroomArt, BlueprintArt, JourneyIcon, HappyHomeArt } from "@/components/RoomArt";
import { toast } from "sonner";
import {
  ArrowRight, ShieldCheck, Clock, IndianRupee, Camera, Sparkles, UserCheck,
  Check, X, Star, Menu, PhoneCall, MapPin, Mail, ChevronRight, PackageCheck,
  FileCheck, Layers, Building2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PROP_TYPES = ["Apartment", "Villa", "Independent House", "Duplex", "Farmhouse", "Office", "Other"];
const BUDGETS = ["Under ₹5 Lakh", "₹5–10 Lakh", "₹10–20 Lakh", "₹20–40 Lakh", "₹40 Lakh+"];
const inp = "w-full rounded-xl border border-[#E3DAC8] bg-[#F5F2EB] px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F] focus:border-transparent transition";
function Field({ label, children }) {
  return <div><label className="text-xs font-semibold uppercase tracking-wider text-[#8a7d63]">{label}</label><div className="mt-1.5">{children}</div></div>;
}

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const NAV = [
  { label: "Why ILLAM", href: "#pain-points" },
  { label: "The Journey", href: "#journey" },
  { label: "What You Get", href: "#features" },
  { label: "Warranty", href: "#warranty" },
  { label: "Reviews", href: "#reviews" },
  { label: "Book", href: "#book" },
];

const PAIN = [
  "Budgets balloon 15–40% mid-project with no warning",
  "Vague hand-drawn quotes hiding material specs",
  "Endless contractor delays and ghosting after advance",
  "Site carpentry with zero structural warranty",
  "No real visibility into procurement or progress",
];
const SOLVE = [
  "Fixed-price contract — zero cost-overrun guarantee",
  "Itemised BOQ: exact brand, thickness & hardware grade",
  "45-day on-time handover, backed by delay penalty",
  "Factory precision + a real 10-year warranty",
  "Live portal — daily site photos & material dispatch",
];

const JOURNEY = [
  { n: "01", t: "Free Consultation", d: "Lifestyle audit, floor-plan analysis and honest budget benchmark." },
  { n: "02", t: "AI 3D Renders & VR", d: "Walk through exact lighting, textures and spatial clearance before we build." },
  { n: "03", t: "Transparent Commercials", d: "Every cabinet, hinge and veneer priced with total clarity — no surprises." },
  { n: "04", t: "Factory Precision", d: "Automated cutting and edge-banding for furniture that lasts decades." },
  { n: "05", t: "Monitored Execution", d: "A dedicated site captain sends daily photo & video updates to your phone." },
  { n: "06", t: "150-Point Snagging", d: "A strict quality inspection before we ever hand over your keys." },
  { n: "07", t: "10-Year Warranty", d: "Post-handover care and annual service, all managed from your portal." },
];

const FEATURES = [
  { icon: Camera, badge: "Customer Portal", t: "Live Site Timeline", d: "Watch your kitchen cabinets assembled in real time — from anywhere in the world." },
  { icon: Sparkles, badge: "AI Commercial Engine", t: "Instant Re-Costing", d: "Switch quartz to Italian marble and see your BOQ update instantly. No guesswork." },
  { icon: ShieldCheck, badge: "Peace of Mind", t: "10-Year Warranty", d: "Hettich & Blum hardware on marine-grade BWP ply, backed by an official certificate." },
  { icon: UserCheck, badge: "One Point of Contact", t: "Your Project Captain", d: "One dedicated manager — never juggle ten carpenters, plumbers and electricians again." },
];

const STATS = [
  { v: "1,250+", l: "Homes Crafted" },
  { v: "99.4%", l: "On-Time Delivery" },
  { v: "10 Yrs", l: "Warranty" },
  { v: "4.9/5", l: "Homeowner Rating" },
];

const REVIEWS = [
  { name: "Priya & Karthik", city: "Chennai", text: "The live portal changed everything. We tracked every milestone from Bangalore and moved into a flawless home — exactly on the promised date." },
  { name: "Deepa Rangan", city: "Coimbatore", text: "Fixed price meant fixed price. Not a single hidden charge. The BOQ listed every hinge and its brand. I've never trusted a contractor like this." },
  { name: "Arun Prakash", city: "Madurai", text: "The AI renders were spot-on to the final result. What I saw in VR is exactly the home I live in today. Handover was spotless." },
];

function useReveal() {
  const [seen, setSeen] = useState({});
  useEffect(() => {
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => e.isIntersecting && setSeen((s) => ({ ...s, [e.target.id]: true }))),
      { threshold: 0.15 }
    );
    document.querySelectorAll("[data-reveal]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return seen;
}

export default function Landing() {
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [form, setForm] = useState({ first_name: "", phone: "", email: "", city: "", property_type: "", budget_range: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [ref, setRef] = useState("");
  useReveal();

  const goStaff = () => navigate("/login");
  const goPortal = () => navigate("/portal");
  const scrollTo = (href) => { setMenu(false); document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }); };
  const goBook = () => scrollTo("#book");
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submitBooking = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/public/consultation`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong");
      setRef(data.code || ""); setSent(true);
      toast.success("Consultation request sent!");
    } catch (err) {
      toast.error(err.message || "Could not submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F2EB] text-[#3E3E3E] antialiased" style={{ scrollBehavior: "smooth" }} data-testid="landing-page">
      {/* NAV */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#F5F2EB]/85 border-b border-[#E3DAC8]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-[68px] flex items-center justify-between">
          <button onClick={() => scrollTo("#top")} data-testid="nav-logo" className="cursor-pointer flex items-center gap-3"><IllamAppIcon size={38} /><IllamLogo /></button>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <button key={n.href} onClick={() => scrollTo(n.href)} data-testid={`nav-link-${n.href.slice(1)}`}
                className="text-sm font-medium text-[#5A5A5A] hover:text-[#3E3E3E] transition-colors">{n.label}</button>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={goPortal} data-testid="nav-customer-portal-btn"
              className="text-sm font-semibold px-5 py-2 rounded-full bg-[#F56B1F] text-white hover:bg-[#D65A10] transition-colors">Customer Portal</button>
          </div>
          <button className="md:hidden p-2" onClick={() => setMenu((m) => !m)} data-testid="nav-mobile-toggle"><Menu size={22} /></button>
        </div>
        {menu && (
          <div className="md:hidden border-t border-[#E3DAC8] bg-[#F5F2EB] px-5 py-4 space-y-3" data-testid="nav-mobile-menu">
            {NAV.map((n) => <button key={n.href} onClick={() => scrollTo(n.href)} className="block w-full text-left text-sm font-medium text-[#5A5A5A] py-1">{n.label}</button>)}
            <div className="flex gap-3 pt-2">
              <button onClick={goPortal} data-testid="nav-customer-portal-btn-mobile" className="flex-1 text-sm font-semibold px-4 py-2.5 rounded-full bg-[#F56B1F] text-white">Customer Portal</button>
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="top" className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-14 md:pt-20 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div className="lg:col-span-6" initial="hidden" animate="show" variants={fade}>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#EEE9DE] border border-[#E3DAC8] px-4 py-1.5 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F56B1F]" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a7d63]">Turnkey Interior Design & Execution</span>
            </div>
            <h1 data-testid="hero-headline" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Your dream home,<br />built on a <span className="text-[#F56B1F]">100% price</span><br />& time guarantee.
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#5A5A5A] leading-relaxed max-w-xl">
              No hidden costs. No endless delays. From AI-powered 3D renders to factory-finished woodwork and a 10-year warranty — track every single nail from your phone.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button onClick={goBook} data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-[#F56B1F] hover:bg-[#D65A10] text-white font-semibold px-6 py-3.5 shadow-sm transition-colors">
                Book Free Consultation <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={goPortal} data-testid="hero-customer-portal-cta"
                className="inline-flex items-center gap-2 rounded-full border border-[#D7C6A9] hover:border-[#3E3E3E] font-semibold px-6 py-3.5 transition-colors">
                Customer Portal
              </button>
            </div>
            <div data-testid="hero-trust-badge" className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-[#5A5A5A]">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={16} className="text-[#F56B1F]" /> 10-Year Warranty</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={16} className="text-[#F56B1F]" /> 45-Day Delivery</span>
              <span className="inline-flex items-center gap-1.5"><IndianRupee size={16} className="text-[#F56B1F]" /> 0% Cost Overrun</span>
            </div>
          </motion.div>

          <motion.div className="lg:col-span-6" initial="hidden" animate="show" custom={1} variants={fade}>
            <div className="relative">
              <div className="rounded-[28px] overflow-hidden shadow-2xl ring-1 ring-[#E3DAC8] bg-[#F5F2EB]">
                <LivingRoomArt className="w-full h-[380px] md:h-[520px]" />
              </div>
              <div className="absolute -bottom-6 -left-4 md:left-6 bg-white rounded-2xl shadow-xl border border-[#E3DAC8] px-5 py-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#EEE9DE] flex items-center justify-center"><IllamMark size={22} /></div>
                <div>
                  <div className="font-heading font-bold text-lg leading-none">1,250+</div>
                  <div className="text-xs text-[#8a7d63] mt-1">homes handed over</div>
                </div>
              </div>
              <div className="absolute -top-4 right-4 bg-[#3E3E3E] text-[#F5F2EB] rounded-2xl shadow-xl px-4 py-3 hidden sm:flex items-center gap-2">
                <Star size={16} className="text-[#F56B1F] fill-[#F56B1F]" />
                <span className="text-sm font-semibold">4.9 / 5 rating</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="bg-[#3E3E3E] text-[#F5F2EB]" data-testid="stats-band">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.l} className="text-center md:text-left">
              <div className="font-heading text-3xl md:text-4xl font-extrabold text-[#F56B1F]">{s.v}</div>
              <div className="text-sm text-[#b9b2a3] mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PAIN POINTS VS ILLAM */}
      <section id="pain-points" data-reveal data-testid="pain-points-section" className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F56B1F] mb-3">Why homeowners switch</div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">The old way is broken.<br />We rebuilt it around you.</h2>
          <p className="mt-4 text-[#5A5A5A]">Renovating a home shouldn't feel like a gamble. Here's the difference the day you choose ILLAM.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div data-testid="comparison-traditional-card" className="rounded-3xl border border-[#E3DAC8] bg-white p-7 md:p-9">
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#9a9a9a] mb-6"><X size={18} /> The Traditional Nightmare</div>
            <ul className="space-y-4">
              {PAIN.map((p) => (
                <li key={p} className="flex items-start gap-3 text-[#5A5A5A]">
                  <span className="mt-1 w-5 h-5 rounded-full bg-[#f2eadf] flex items-center justify-center shrink-0"><X size={12} className="text-[#b9563a]" /></span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div data-testid="comparison-illam-card" className="rounded-3xl bg-[#3E3E3E] text-[#F5F2EB] p-7 md:p-9 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-[#F56B1F] opacity-10 blur-2xl" />
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#F56B1F] mb-6"><IllamMark size={20} /> The ILLAM Way</div>
            <ul className="space-y-4">
              {SOLVE.map((s) => (
                <li key={s} className="flex items-start gap-3">
                  <span className="mt-1 w-5 h-5 rounded-full bg-[#F56B1F] flex items-center justify-center shrink-0"><Check size={12} className="text-white" /></span>
                  <span className="text-[#e9e3d6]">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section id="journey" data-testid="journey-section" className="bg-[#EEE9DE] border-y border-[#E3DAC8]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F56B1F] mb-3">Consultation to keys</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Seven clear steps to your finished home</h2>
            <p className="mt-4 text-[#5A5A5A]">One team, one plan, total transparency — you always know exactly where your project stands.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {JOURNEY.map((j, i) => (
              <div key={j.n} data-testid={`journey-step-${i + 1}`} className="rounded-2xl bg-white border border-[#E3DAC8] p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-[#EEE9DE] flex items-center justify-center"><JourneyIcon step={i + 1} /></div>
                  <span className="font-heading text-2xl font-extrabold text-[#D7C6A9]">{j.n}</span>
                </div>
                <div className="font-heading font-bold text-lg mt-4">{j.t}</div>
                <p className="text-sm text-[#5A5A5A] mt-2 leading-relaxed">{j.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES + IMAGERY */}
      <section id="features" data-testid="features-bento-grid" className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F56B1F] mb-3">What you actually get</div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Built for your peace of mind</h2>
        </div>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 rounded-3xl overflow-hidden relative min-h-[320px] group">
            <KitchenArt className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A2A2A]/85 via-[#2A2A2A]/20 to-transparent" />
            <div className="absolute bottom-0 p-7 text-[#F5F2EB]">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#F5b98a]">{FEATURES[0].badge}</span>
              <h3 className="font-heading text-2xl font-bold mt-2">{FEATURES[0].t}</h3>
              <p className="text-sm text-[#e9e3d6] mt-2 max-w-md">{FEATURES[0].d}</p>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
            {FEATURES.slice(1).map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.t} data-testid={`feature-card-${f.t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="rounded-3xl bg-white border border-[#E3DAC8] p-6 hover:shadow-lg transition-shadow">
                  <div className="w-11 h-11 rounded-xl bg-[#EEE9DE] flex items-center justify-center"><Icon size={20} className="text-[#F56B1F]" /></div>
                  <span className="block text-[10px] font-semibold uppercase tracking-widest text-[#8a7d63] mt-4">{f.badge}</span>
                  <h3 className="font-heading font-bold text-lg mt-1">{f.t}</h3>
                  <p className="text-sm text-[#5A5A5A] mt-1.5 leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WARRANTY / PORTAL SPLIT */}
      <section id="warranty" data-testid="portal-access-section" className="bg-[#3E3E3E] text-[#F5F2EB]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F56B1F] mb-3">Two ways in</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">One platform. Total clarity for everyone.</h2>
            <p className="mt-4 text-[#b9b2a3] max-w-md">Homeowners track their build in real time. Our team runs every project with military precision. Pick your door in.</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-[#333333] border border-[#4a4a4a] p-6">
                <Building2 size={22} className="text-[#F56B1F]" />
                <h3 className="font-heading font-bold text-lg mt-3">Customer Portal</h3>
                <ul className="mt-3 space-y-2 text-sm text-[#c9c2b3]">
                  {["Live milestone timeline", "Approve designs & swatches", "Download invoices & warranty", "Raise service tickets"].map((x) => (
                    <li key={x} className="flex items-start gap-2"><Check size={14} className="text-[#F56B1F] mt-0.5 shrink-0" />{x}</li>
                  ))}
                </ul>
                <button onClick={goPortal} data-testid="customer-login-button"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F56B1F] hover:bg-[#D65A10] text-white font-semibold py-3 transition-colors">
                  Enter Portal <ChevronRight size={16} />
                </button>
              </div>
              <div className="rounded-2xl bg-[#333333] border border-[#4a4a4a] p-6">
                <Layers size={22} className="text-[#F5F2EB]" />
                <h3 className="font-heading font-bold text-lg mt-3">Staff Login</h3>
                <ul className="mt-3 space-y-2 text-sm text-[#c9c2b3]">
                  {["Lead pipeline & site visits", "Procurement & dispatch", "Milestone payout approvals", "Snag & quality manager"].map((x) => (
                    <li key={x} className="flex items-start gap-2"><Check size={14} className="text-[#F5F2EB] mt-0.5 shrink-0" />{x}</li>
                  ))}
                </ul>
                <button onClick={goStaff} data-testid="staff-login-button"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F5F2EB] hover:bg-white text-[#3E3E3E] font-semibold py-3 transition-colors">
                  Team Sign In <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <BedroomArt className="rounded-2xl h-56 md:h-72 w-full object-cover mt-8" />
            <BlueprintArt className="rounded-2xl h-56 md:h-72 w-full object-cover" />
            <div className="col-span-2 rounded-2xl bg-[#333333] border border-[#4a4a4a] p-6 flex items-center gap-4">
              <ShieldCheck size={40} className="text-[#F56B1F] shrink-0" />
              <div>
                <div className="font-heading font-bold text-lg">10-Year Structural Warranty</div>
                <p className="text-sm text-[#b9b2a3] mt-1">Marine-grade BWP ply and premium German hardware, certified in writing.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" data-testid="testimonials-section" className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F56B1F] mb-3">Verified homeowners</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Homes that keep their promises</h2>
            <p className="mt-4 text-[#5A5A5A] max-w-lg">Real families across Tamil Nadu who moved in on time, on budget, and stayed for the warranty.</p>
          </div>
          <div className="lg:col-span-5">
            <HappyHomeArt className="w-full max-w-sm mx-auto" />
          </div>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {REVIEWS.map((r) => (
            <div key={r.name} className="rounded-3xl bg-white border border-[#E3DAC8] p-7 flex flex-col">
              <div className="flex gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} size={16} className="text-[#F56B1F] fill-[#F56B1F]" />)}</div>
              <p className="text-[#5A5A5A] leading-relaxed flex-1">“{r.text}”</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#EEE9DE] flex items-center justify-center font-heading font-bold text-[#8a7d63]">{r.name[0]}</div>
                <div>
                  <div className="font-semibold text-sm">{r.name}</div>
                  <div className="text-xs text-[#8a7d63] flex items-center gap-1"><MapPin size={11} /> {r.city}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BOOK CONSULTATION */}
      <section id="book" data-testid="book-section" className="max-w-7xl mx-auto px-5 md:px-10 pb-20 md:pb-28">
        <div className="rounded-[32px] bg-[#3E3E3E] text-[#F5F2EB] p-7 md:p-14 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#F56B1F] opacity-10 blur-3xl" />
          <div className="relative">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F56B1F] mb-3">Free · no obligation</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Book your free design consultation</h2>
            <p className="mt-4 text-[#b9b2a3] max-w-md">Tell us about your space and we'll call you back with an AI-powered 3D concept and a transparent, fixed-price estimate.</p>
            <ul className="mt-6 space-y-3 text-sm text-[#c9c2b3]">
              {["A dedicated design expert calls within 24 hours", "A free AI 3D concept of your space", "A fixed-price, itemised estimate — no hidden charges"].map((x) => (
                <li key={x} className="flex items-start gap-2"><Check size={16} className="text-[#F56B1F] mt-0.5 shrink-0" />{x}</li>
              ))}
            </ul>
          </div>
          <div className="relative">
            {sent ? (
              <div data-testid="book-success" className="rounded-3xl bg-white text-[#3E3E3E] p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-[#EEE9DE] mx-auto flex items-center justify-center"><Check size={28} className="text-[#F56B1F]" /></div>
                <h3 className="font-heading text-xl font-bold mt-4">Thank you, {form.first_name || "friend"}!</h3>
                <p className="text-sm text-[#5A5A5A] mt-2">Your consultation request is in. Our design team will reach out within 24 hours{ref ? <> · Ref <span className="mono font-semibold">{ref}</span></> : ""}.</p>
                <button onClick={() => { setSent(false); setRef(""); setForm({ first_name: "", phone: "", email: "", city: "", property_type: "", budget_range: "", notes: "" }); }} data-testid="book-another-btn" className="mt-6 text-sm font-semibold text-[#F56B1F] hover:underline">Book another consultation</button>
              </div>
            ) : (
              <form onSubmit={submitBooking} data-testid="consultation-form" className="rounded-3xl bg-white text-[#3E3E3E] p-6 md:p-8 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Your name*"><input required value={form.first_name} onChange={setF("first_name")} data-testid="book-name-input" className={inp} placeholder="Priya" /></Field>
                  <Field label="Phone*"><input required value={form.phone} onChange={setF("phone")} data-testid="book-phone-input" className={inp} placeholder="+91 98765 43210" /></Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Email"><input type="email" value={form.email} onChange={setF("email")} data-testid="book-email-input" className={inp} placeholder="you@email.com" /></Field>
                  <Field label="City"><input value={form.city} onChange={setF("city")} data-testid="book-city-input" className={inp} placeholder="Chennai" /></Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Property type"><select value={form.property_type} onChange={setF("property_type")} data-testid="book-property-select" className={inp}><option value="">Select</option>{PROP_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
                  <Field label="Budget"><select value={form.budget_range} onChange={setF("budget_range")} data-testid="book-budget-select" className={inp}><option value="">Select</option>{BUDGETS.map((x) => <option key={x} value={x}>{x}</option>)}</select></Field>
                </div>
                <Field label="Anything else?"><textarea rows={3} value={form.notes} onChange={setF("notes")} data-testid="book-notes-input" className={inp} placeholder="Tell us about your space or timeline" /></Field>
                <button type="submit" disabled={loading} data-testid="book-submit-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F56B1F] hover:bg-[#D65A10] disabled:opacity-60 text-white font-semibold py-3.5 transition-colors">
                  {loading ? "Sending…" : "Request my free consultation"} {!loading && <ArrowRight size={18} />}
                </button>
                <p className="text-xs text-[#8a7d63] text-center">Your details go straight to our design team. We never share them.</p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer data-testid="footer-section" className="bg-[#2A2A2A] text-[#F5F2EB]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div data-testid="footer-logo" className="flex items-center gap-3"><IllamAppIcon size={44} /><IllamLogo dark /></div>
            <p className="text-sm text-[#b9b2a3] mt-4 max-w-sm leading-relaxed">
              End-to-end interior architecture & turnkey home execution. Designed by AI, crafted with precision, guaranteed for a decade. Based in Chennai, serving all of Tamil Nadu.
            </p>
          </div>
          <div>
            <div className="font-heading font-bold mb-4">Company</div>
            <ul className="space-y-2 text-sm text-[#b9b2a3]">
              {NAV.map((n) => <li key={n.href}><button onClick={() => scrollTo(n.href)} className="hover:text-[#F56B1F] transition-colors">{n.label}</button></li>)}
            </ul>
          </div>
          <div>
            <div className="font-heading font-bold mb-4">Get in touch</div>
            <ul className="space-y-3 text-sm text-[#b9b2a3]">
              <li className="flex items-center gap-2"><PhoneCall size={15} className="text-[#F56B1F]" /> +91 90000 00000</li>
              <li data-testid="footer-customer-support-link" className="flex items-center gap-2"><Mail size={15} className="text-[#F56B1F]" /> care@illam.in</li>
              <li className="flex items-center gap-2"><MapPin size={15} className="text-[#F56B1F]" /> Chennai (Head Office) · Serving all Tamil Nadu</li>
            </ul>
            <div className="flex gap-3 mt-5">
              <button onClick={goPortal} data-testid="footer-customer-portal-btn" className="text-xs font-semibold px-3 py-2 rounded-full border border-[#4a4a4a] hover:border-[#F56B1F] transition-colors">Customer Portal</button>
              <button onClick={goStaff} data-testid="footer-staff-login-btn" className="text-xs font-semibold px-3 py-2 rounded-full border border-[#4a4a4a] hover:border-[#F56B1F] transition-colors">Staff Login</button>
            </div>
          </div>
        </div>
        <div className="border-t border-[#3E3E3E]">
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#8a8172]">
            <span>© {new Date().getFullYear()} ILLAM · Interior Design & Execution</span>
            <span className="mono">Spaces for a better tomorrow.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
